import styles from './styles.scss';
import { rm, scrollToTarget, NO_ELEMENT } from 'utils';
import { useReducer, useEffect, useRef, ChangeEvent } from 'react';
import { useToggle } from 'hooks';
import { deliveriesInitialState, isValid, reducer } from './DeliveriesHelper';
import {
  addressChanged,
  addressLookupValidationError,
  contactOptionChanged,
  OrderFormProps
} from '../Utils';
import { Address } from 'components/CustomerSupportForms/Components/Address/Address';
import { postSendDeliveryQuestion } from 'services/ApiCustomerSupportFormService';
import {
  BlueSectionContainer,
  ErrorContainer,
  SubmitContainer
} from 'components/CustomerSupportForms/Components/SectionContainer/SectionContainer';
import Spinner from 'components/Shared/Loader';
import { Button, LoaderButton } from 'components/Button';
import { TextArea } from 'components/Shared/Form';
import { IBasicResponse } from 'models/IBasicResponse';
import MessageView from '../Components/MessageView';
import { useDispatch } from 'react-redux';
import { GtmCustomerSupportFormSubmit } from 'store/googleTagManager/actions';
import { FormType } from 'models/FormType';
import { CustomerContact } from '../Components/CustomerContact/CustomerContact';

const QUESTION_MAX_LENGTH = 1000;

export const DeliveriesForm = ({ order, error, returnUrl, onSuccess }: OrderFormProps) => {
  const [internalState, internalDispatch] = useReducer(reducer, deliveriesInitialState);
  const [addressChange, toggleAddressChange] = useToggle(false);
  const anchorRef = useRef(NO_ELEMENT);
  const scrollToTopIfNeeded = scrollToTarget(anchorRef);
  const [loading, toggleLoading] = useToggle(false);
  const reduxDispatch = useDispatch();

  useEffect(() => {
    if (order) {
      internalDispatch({ type: 'address', value: order?.deliveryAddress });
      internalDispatch({ type: 'email', value: order?.email });
    }
    if (error) {
      internalDispatch({
        type: 'hasError',
        value: true
      });
    }
  }, [error, order]);

  useEffect(() => {
    internalDispatch({ type: 'addressError', value: '' });
  }, [addressChange]);

  const handleQuestionInput = (_event: ChangeEvent<HTMLTextAreaElement>, newValue: string) => {
    internalDispatch({
      type: 'question',
      value: newValue
    });
    internalDispatch({
      type: 'questionError',
      value: ''
    });
  };
  const { address, email, contactOption, question } = internalState;

  const completeInternal = () => {
    toggleLoading();
    internalDispatch({ type: 'generalError', value: '' });
    if (isValid(addressChange, internalState, internalDispatch)) {
      if (address && email && order) {
        return postSendDeliveryQuestion({
          address,
          email,
          orderNumber: order.orderNumber,
          question,
          hash: order.hash,
          customerContactOption: contactOption
        })
          .then(() => {
            toggleLoading();
            onSuccess();
            scrollToTopIfNeeded();
            reduxDispatch(GtmCustomerSupportFormSubmit(FormType.Deliveries));
          })
          .catch((response: IBasicResponse) => {
            toggleLoading();
            internalDispatch({ type: 'generalError', value: response.message[0] });
          });
      }
    }

    toggleLoading();
    return Promise.resolve();
  };

  if (internalState.hasError || order?.orderNumber === '' || order?.orderNumber === undefined) {
    return (
      <MessageView
        title={rm('CustomerPage_Return_Error_Title')}
        message={rm('CustomerPage_Return_Error_Msg')}
        returnUrl={returnUrl}
      />
    );
  }

  return (
    <>
      <h3>{rm('CustomerService_Question_Delivery_Address_Header')}</h3>
      <BlueSectionContainer error={internalState.addressError}>
        {internalState.address ? (
          <Address
            address={internalState.address}
            email={internalState.email}
            onChange={(address, email) => addressChanged(address, email, internalDispatch)}
            changeMode={addressChange}
            toggleChange={toggleAddressChange}
            validationError={() => {
              addressLookupValidationError(internalDispatch);
            }}
          />
        ) : (
          <Spinner className={styles.spinner} loading />
        )}
      </BlueSectionContainer>
      {internalState.addressError && <ErrorContainer>{internalState.addressError}</ErrorContainer>}
      <h3>{rm('Customerpage_Deliveries_Question')}</h3>
      <BlueSectionContainer error={internalState.questionError}>
        <span>{rm('Customerpage_Deliveries_Subheader')}</span>
        <TextArea
          onChange={handleQuestionInput}
          textlimit={QUESTION_MAX_LENGTH}
          value={internalState.question}
        />
        <p
          id={styles.returnMsgLength}
        >{`(${internalState.question.length}/${QUESTION_MAX_LENGTH})`}</p>
      </BlueSectionContainer>
      {internalState.questionError && (
        <ErrorContainer>{internalState.questionError}</ErrorContainer>
      )}
      <CustomerContact
        contact={internalState.contactOption}
        onChange={val => contactOptionChanged(val, internalDispatch)}
        error={internalState.contactOptionError}
      />
      <SubmitContainer>
        <LoaderButton appearance="whitePillPrimary" onClick={completeInternal} loading={loading}>
          {loading ? '' : rm('customerpage_returns_submit')}
        </LoaderButton>
        <Button appearance="whitePill" href={returnUrl}>
          {rm('customerpage_returns_cancel')}
        </Button>
      </SubmitContainer>
      {internalState.generalError && (
        <BlueSectionContainer error>{internalState.generalError}</BlueSectionContainer>
      )}
    </>
  );
};
